/**
 * Shared admin form system — the form layout / field architecture migrated from the
 * vendor dashboard. Presentation only: these are styling + structure primitives, they
 * hold no business logic.
 *
 * - `FormSection`  — a titled card section (icon chip + heading + optional description).
 * - `FormGrid`     — responsive field grid (1 / 2 / 3 columns).
 * - `FormField`    — label + control wrapper for native selects / custom controls.
 * - `FormSelect`   — design-system styled native <select>.
 * - `FormTextarea` — design-system styled native <textarea>.
 * - `FormActions`  — bottom action bar (hairline divider + right-aligned buttons).
 * - `FormBanner`   — inline error / success / info banner.
 *
 * Native <select>/<textarea> share the metrics of the @terra/ui `Input` so every
 * control on a form lines up on the same baseline grid.
 */
import { AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import type {
  HTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

import { Card, CardBody, CardHeader, CardTitle, cn } from '@terra/ui';

/** Native control styling — mirrors the @terra/ui Input shell. */
export const FIELD_CLASS =
  'border-border-strong/35 bg-surface text-ink h-11 w-full rounded-xl border px-3.5 text-sm ' +
  'transition-all duration-200 hover:border-border-strong/60 ' +
  'focus:border-gold-400 focus:shadow-focus focus:outline-none ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

export const TEXTAREA_CLASS =
  'border-border-strong/35 bg-surface text-ink w-full rounded-xl border px-3.5 py-2.5 text-sm ' +
  'transition-all duration-200 hover:border-border-strong/60 ' +
  'focus:border-gold-400 focus:shadow-focus focus:outline-none ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

// ── Section card ─────────────────────────────────────────────────────────────

export interface FormSectionProps {
  /** Section heading. */
  title: ReactNode;
  /** Optional one-line helper under the heading. */
  description?: ReactNode;
  /** Icon chip rendered before the title. */
  icon?: ReactNode;
  /** Icon chip accent. */
  accent?: 'gold' | 'red' | 'accent' | 'neutral';
  /** Right-aligned header content (e.g. a toggle or badge). */
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
  /** CardBody className override (defaults to a vertical-rhythm stack). */
  bodyClassName?: string;
}

const ICON_ACCENT: Record<NonNullable<FormSectionProps['accent']>, string> = {
  gold: 'bg-gold-50 text-gold-700 ring-gold-100',
  red: 'bg-red-50 text-red-600 ring-red-100',
  accent: 'bg-accent-50 text-accent-600 ring-accent-100',
  neutral: 'bg-surface-subtle text-ink-muted ring-border',
};

export function FormSection({
  title,
  description,
  icon,
  accent = 'gold',
  aside,
  children,
  className,
  bodyClassName,
}: FormSectionProps) {
  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex min-w-0 items-center gap-2.5">
          {icon && (
            <span
              className={cn(
                'grid h-8 w-8 shrink-0 place-items-center rounded-lg ring-1 ring-inset',
                ICON_ACCENT[accent],
              )}
            >
              {icon}
            </span>
          )}
          <div className="min-w-0">
            <CardTitle>{title}</CardTitle>
            {description && (
              <p className="text-ink-muted mt-0.5 text-xs leading-relaxed">{description}</p>
            )}
          </div>
        </div>
        {aside}
      </CardHeader>
      <CardBody className={cn('space-y-5', bodyClassName)}>{children}</CardBody>
    </Card>
  );
}

// ── Field grid ───────────────────────────────────────────────────────────────

export interface FormGridProps extends HTMLAttributes<HTMLDivElement> {
  /** Column count at the `sm` breakpoint and up. */
  columns?: 1 | 2 | 3;
}

const GRID_COLS: Record<NonNullable<FormGridProps['columns']>, string> = {
  1: '',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-2 lg:grid-cols-3',
};

export function FormGrid({ columns = 2, className, ...rest }: FormGridProps) {
  return <div className={cn('grid gap-5', GRID_COLS[columns], className)} {...rest} />;
}

/** Spans the full width inside a `FormGrid`. */
export function FormFull({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('sm:col-span-2 lg:col-span-3', className)} {...rest} />;
}

// ── Label + control wrapper ──────────────────────────────────────────────────

export interface FormFieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  htmlFor?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
}

export function FormField({
  label,
  hint,
  error,
  htmlFor,
  required,
  children,
  className,
}: FormFieldProps) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <label htmlFor={htmlFor} className="text-ink text-[13px] font-semibold">
          {label}
          {required && <span className="text-state-danger ml-0.5">*</span>}
        </label>
      )}
      {children}
      {error ? (
        <p className="text-state-danger text-xs font-medium">{error}</p>
      ) : hint ? (
        <p className="text-ink-subtle text-xs">{hint}</p>
      ) : null}
    </div>
  );
}

// ── Styled native select ─────────────────────────────────────────────────────

export interface FormSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
}

export function FormSelect({ label, hint, error, className, id, ...rest }: FormSelectProps) {
  return (
    <FormField
      {...(label !== undefined ? { label } : {})}
      {...(hint !== undefined ? { hint } : {})}
      {...(error !== undefined ? { error } : {})}
      {...(id !== undefined ? { htmlFor: id } : {})}
      {...(rest.required !== undefined ? { required: rest.required } : {})}
    >
      <select
        id={id}
        className={cn(FIELD_CLASS, error && 'border-state-danger', className)}
        {...rest}
      />
    </FormField>
  );
}

// ── Styled native textarea ───────────────────────────────────────────────────

export interface FormTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
}

export function FormTextarea({ label, hint, error, className, id, ...rest }: FormTextareaProps) {
  return (
    <FormField
      {...(label !== undefined ? { label } : {})}
      {...(hint !== undefined ? { hint } : {})}
      {...(error !== undefined ? { error } : {})}
      {...(id !== undefined ? { htmlFor: id } : {})}
      {...(rest.required !== undefined ? { required: rest.required } : {})}
    >
      <textarea
        id={id}
        rows={rest.rows ?? 4}
        className={cn(TEXTAREA_CLASS, error && 'border-state-danger', className)}
        {...rest}
      />
    </FormField>
  );
}

// ── Action bar ───────────────────────────────────────────────────────────────

export interface FormActionsProps extends HTMLAttributes<HTMLDivElement> {
  /** Left-aligned content (e.g. a hint or secondary text). */
  hint?: ReactNode;
  /** Makes the bar stick to the bottom of the viewport. */
  sticky?: boolean;
}

export function FormActions({ hint, sticky, className, children, ...rest }: FormActionsProps) {
  return (
    <div
      className={cn(
        'border-border flex flex-wrap items-center gap-3 border-t pt-4',
        hint ? 'justify-between' : 'justify-end',
        sticky &&
          'bg-surface/85 sticky bottom-0 z-10 -mx-4 -mb-4 px-4 pb-4 backdrop-blur-xl md:-mx-6 md:px-6 lg:-mx-8 lg:px-8',
        className,
      )}
      {...rest}
    >
      {hint && <p className="text-ink-subtle text-xs">{hint}</p>}
      <div className="flex flex-wrap items-center gap-2.5">{children}</div>
    </div>
  );
}

// ── Inline banner ────────────────────────────────────────────────────────────

export interface FormBannerProps {
  tone?: 'error' | 'success' | 'info';
  children: ReactNode;
  className?: string;
}

const BANNER_TONE = {
  error: 'border-state-danger/30 bg-state-danger-bg text-state-danger',
  success: 'border-gold-200 bg-state-success-bg text-state-success',
  info: 'border-border bg-surface-subtle text-ink-muted',
} as const;

const BANNER_ICON = {
  error: AlertTriangle,
  success: CheckCircle2,
  info: Info,
} as const;

export function FormBanner({ tone = 'error', children, className }: FormBannerProps) {
  const Icon = BANNER_ICON[tone];
  return (
    <div
      className={cn(
        'flex items-start gap-2.5 rounded-xl border p-4 text-sm font-medium',
        BANNER_TONE[tone],
        className,
      )}
    >
      <Icon className="mt-px h-4 w-4 shrink-0" />
      <div className="min-w-0 [overflow-wrap:anywhere]">{children}</div>
    </div>
  );
}
